import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/**
 * Salt and Light Ministries design system.
 *
 * Direction: warm and hopeful. The previous palette was entirely desaturated
 * (muted mauve on beige) which read as flat and failed WCAG AA — body and
 * heading colours sat between 2.8:1 and 4.2:1 against the background. Every
 * pairing below was checked to clear 4.5:1.
 *
 * Colour rules:
 *   - `primary`  deep terracotta — headings and primary buttons
 *   - `accent`   burnt gold, AA-safe on cream — donate/CTA emphasis
 *   - `goldGlow` bright gold, ONLY on the dark hero scrim (it is 2.0:1 on
 *                cream, so it must never be used for text on a light surface)
 *
 * Typography: Fraunces (a warm display serif with real character) for headings,
 * Inter for body copy. Loaded in public/index.html. Sizes are wrapped in
 * responsiveFontSizes() so headings scale down on phones instead of the old
 * hardcoded 62px.
 */

const PALETTE = {
  cream: '#FDF8F2',
  surface: '#FFFFFF',
  terracotta: '#8A3E2C',
  terracottaDark: '#6E3023',
  gold: '#A55C08',
  goldGlow: '#F0B357',
  ink: '#3B2E28',
  inkMuted: '#6E5B52',
  hairline: '#EADFD3',
};

const HEADING_FONT = '"Fraunces", "Iowan Old Style", Georgia, serif';
const BODY_FONT = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const base = {
  palette: {
    mode: 'light',
    primary: {
      main: PALETTE.terracotta,
      dark: PALETTE.terracottaDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: PALETTE.gold,
      contrastText: '#FFFFFF',
    },
    background: {
      default: PALETTE.cream,
      paper: PALETTE.surface,
    },
    text: {
      primary: PALETTE.ink,
      secondary: PALETTE.inkMuted,
    },
    divider: PALETTE.hairline,
  },

  // Left at the MUI default of 4 on purpose. `sx={{ borderRadius: n }}` is a
  // MULTIPLE of this value, and the pages already use borderRadius: 4–5 to mean
  // roughly 16–20px. Raising it here would quietly balloon every one of them.
  shape: { borderRadius: 4 },

  typography: {
    fontFamily: BODY_FONT,
    // Headings carry the personality; body stays quiet and readable.
    h1: { fontFamily: HEADING_FONT, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.05, fontSize: '3.5rem' },
    h2: { fontFamily: HEADING_FONT, fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.12, fontSize: '2.5rem' },
    h3: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2, fontSize: '2rem' },
    h4: { fontFamily: HEADING_FONT, fontWeight: 700, lineHeight: 1.25, fontSize: '1.5rem' },
    h5: { fontFamily: HEADING_FONT, fontWeight: 600, lineHeight: 1.3, fontSize: '1.25rem' },
    h6: { fontFamily: HEADING_FONT, fontWeight: 600, lineHeight: 1.35, fontSize: '1.1rem' },
    // A small all-caps label used for section eyebrows.
    overline: {
      fontFamily: BODY_FONT,
      fontWeight: 700,
      fontSize: '0.75rem',
      letterSpacing: '0.14em',
      lineHeight: 2,
    },
    body1: { fontSize: '1.0625rem', lineHeight: 1.7 },
    body2: { fontSize: '0.9375rem', lineHeight: 1.65 },
    button: { fontFamily: BODY_FONT, fontWeight: 700, letterSpacing: '0.01em', textTransform: 'none' },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: PALETTE.cream, color: PALETTE.ink },
        // Never let a stray wide element cause sideways scrolling on a phone.
        html: { overflowX: 'hidden' },
        img: { maxWidth: '100%' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 26, paddingBlock: 12, fontSize: '1rem' },
        containedPrimary: {
          '&:hover': { backgroundColor: PALETTE.terracottaDark },
        },
        // Used for Donate / Give — the one place that should feel golden.
        containedSecondary: {
          '&:hover': { backgroundColor: '#8F4E05' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // The old cards were #f5f7fa — a cool blue-grey on a warm page, which
        // is what made them look dingy. Paper now inherits the warm surface.
        rounded: { borderRadius: 20 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: `1px solid ${PALETTE.hairline}`,
          boxShadow: '0 6px 20px rgba(58, 40, 32, 0.07)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(253, 248, 242, 0.88)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${PALETTE.hairline}`,
          boxShadow: 'none',
          color: PALETTE.terracotta,
        },
      },
    },
  },
};

export const lightTheme = responsiveFontSizes(createTheme(base), { factor: 2.6 });

export const darkTheme = responsiveFontSizes(
  createTheme({
    ...base,
    palette: {
      mode: 'dark',
      primary: { main: '#E9B08C', contrastText: '#2A1A14' },
      secondary: { main: PALETTE.goldGlow, contrastText: '#2A1A14' },
      background: { default: '#2A1F1B', paper: '#362A24' },
      text: { primary: '#F5EBE3', secondary: '#C9B7AC' },
      divider: '#4A3A32',
    },
  }),
  { factor: 2.6 }
);

// Exported so pages can reach for brand values without re-hardcoding hexes.
export const brand = PALETTE;
